/**
 * Shared Haversine distance utilities.
 * Single canonical implementation used across geo-core, data-pipeline, and validation.
 *
 * Return units:
 * - haversine() → meters (canonical)
 * - haversineKm() → kilometers
 * - haversineMiles() → miles
 */

/** Earth's mean radius in meters (WGS84 approximation). */
const EARTH_RADIUS_METERS = 6371e3;

/** Meters per mile (single source of truth for shared haversine consumers). */
export const METERS_PER_MILE = 1609.34;

export interface HaversineParams {
  lat1: number;
  lon1: number;
  lat2: number;
  lon2: number;
}

/**
 * Converts degrees to radians.
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Computes great-circle distance between two WGS84 points using the Haversine formula.
 * Handles edge cases: identical points (0), antipodal points (~20,037 km), poles, antimeridian.
 *
 * @param params - { lat1, lon1, lat2, lon2 } in degrees (lat -90..90, lon -180..180)
 * @returns Distance in **meters** (canonical unit)
 */
export function haversine(params: HaversineParams): number {
  const { lat1, lon1, lat2, lon2 } = params;
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const deltaPhi = toRadians(lat2 - lat1);
  const deltaLambda = toRadians(lon2 - lon1);

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const clampedA = Math.min(1, a);
  const c = 2 * Math.atan2(Math.sqrt(clampedA), Math.sqrt(1 - clampedA));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Haversine distance in kilometers.
 * @param params - { lat1, lon1, lat2, lon2 } in degrees
 * @returns Distance in **kilometers**
 */
export function haversineKm(params: HaversineParams): number {
  return haversine(params) / 1000;
}

/**
 * Haversine distance in miles.
 * @param params - { lat1, lon1, lat2, lon2 } in degrees
 * @returns Distance in **miles**
 */
export function haversineMiles(params: HaversineParams): number {
  return haversine(params) / METERS_PER_MILE;
}
